import { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Modal from './Modal';
import Form from '../Form/Form';
import InputText from '../Form/Fields/InputText';
import InputPhone from '../Form/Fields/InputPhone';
import InputDate from '../Form/Fields/InputDate';
import Textarea from '../Form/Fields/Textarea';
import { API_BASE_URL } from '../../api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightLong, faCircleCheck, faCheck, faLock, faPhoneVolume } from '@fortawesome/free-solid-svg-icons';
import './ScheduleCallModal.scss';

const PERK_KEYS = ['free', 'no_commitment', 'personalized'];

const Label = ({ children, required }) => (
  <label>
    {children}
    {required && <span className="syd-schedule-call__required">*</span>}
  </label>
);

export default function ScheduleCallModal({ show, onClose }) {
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const handleClose = () => {
    onClose();
    // Reset after the close animation instead of mid-close, so the form
    // doesn't visibly flash back to empty while the modal is fading out.
    // Remounting the Form (via key) clears its internal react-hook-form state.
    setTimeout(() => {
      setSubmitted(false);
      setFormKey((k) => k + 1);
    }, 300);
  };

  const submit = (data) => {
    setSending(true);
    const message = [
      data.timeSlot && `Créneau souhaité : ${new Date(data.timeSlot).toLocaleString('fr-FR')}`,
      data.profession && `Profession : ${data.profession}`,
      data.notes && `Notes : ${data.notes}`,
    ].filter(Boolean).join('\n');

    axios.post(`${API_BASE_URL}/api/v1/contact`, {
      name: data.name,
      email: data.email,
      phone: data.phone,
      pharmacy: null,
      message: `[Demande d'appel] ${message}`,
    })
      .then(() => setSubmitted(true))
      .catch(() => toast.error(t('landing.schedule_call.error')))
      .finally(() => setSending(false));
  };

  return (
    <Modal
      id="scheduleDemoModal"
      title={t('landing.schedule_call.title')}
      show={show}
      onClose={handleClose}
      icon={faPhoneVolume}
      iconBg="#eff6ff"
      iconColor="#2563eb"
    >
      {submitted ? (
        <div className="syd-schedule-call__sent">
          <div className="syd-schedule-call__sent-icon">
            <FontAwesomeIcon icon={faCircleCheck} />
          </div>
          <h3>{t('landing.schedule_call.sent_title')}</h3>
          <p>{t('landing.schedule_call.sent_desc')}</p>
        </div>
      ) : (
        <Form key={formKey} id="scheduleCallForm" onSubmit={submit} className="syd-schedule-call__form">
          <div className="syd-schedule-call__intro">
            <div className="syd-schedule-call__advisor">
              <img src="/images/contact/call-center-advisor.jpg" alt="" width="56" height="56" />
              <span className="syd-schedule-call__advisor-status" aria-hidden="true" />
            </div>
            <div>
              <p>{t('landing.schedule_call.intro')}</p>
              <ul className="syd-schedule-call__perks">
                {PERK_KEYS.map((key) => (
                  <li key={key}>
                    <FontAwesomeIcon icon={faCheck} />
                    {t(`landing.schedule_call.perk_${key}`)}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="syd-schedule-call__row">
            <div className="syd-schedule-call__field">
              <Label required>{t('landing.schedule_call.full_name')}</Label>
              <InputText name="name" placeholder="Yassine El Amrani" validationRules={{ required: t('landing.schedule_call.field_required') }} />
            </div>
            <div className="syd-schedule-call__field">
              <Label required>{t('Email')}</Label>
              <InputText
                name="email"
                type="email"
                placeholder="email@exemple.com"
                validationRules={{ required: t('landing.schedule_call.field_required') }}
              />
            </div>
          </div>

          <div className="syd-schedule-call__row">
            <div className="syd-schedule-call__field">
              <Label required>{t('landing.schedule_call.phone_number')}</Label>
              <InputPhone name="phone" placeholder="+212 6 00 00 00 00" validationRules={{ required: t('landing.schedule_call.field_required') }} />
            </div>
            <div className="syd-schedule-call__field">
              <Label>{t('landing.schedule_call.profession')}</Label>
              <InputText name="profession" placeholder="Pharmacien titulaire" />
            </div>
          </div>

          <div className="syd-schedule-call__field">
            <Label>{t('landing.schedule_call.time_slot')}</Label>
            <InputDate name="timeSlot" mode="datetime" placeholder="Choisissez une date et une heure" />
          </div>

          <div className="syd-schedule-call__field">
            <Label>{t('landing.schedule_call.notes')}</Label>
            <Textarea name="notes" placeholder="Une question, un besoin particulier ?" />
          </div>

          <button type="submit" className="syd-schedule-call__submit" disabled={sending}>
            {sending ? t('landing.schedule_call.scheduling') : t('landing.schedule_call.submit')}
            <FontAwesomeIcon icon={faArrowRightLong} />
          </button>

          <p className="syd-schedule-call__privacy">
            <FontAwesomeIcon icon={faLock} />
            {t('landing.schedule_call.privacy')}
          </p>
        </Form>
      )}
    </Modal>
  );
}
